import Button from 'components/ui/button';
import { KeyboardEvent as ReactKeyboardEvent, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { IoIosClose } from 'react-icons/io';
import { MdQuiz } from 'react-icons/md';

import './styles.scss';
import { useAuth } from 'context/AuthContext';
import { QuizType, finishQuiz, getQuizQuestions, sendQuestions, startQuiz, verifyQuiz } from 'services/quiz.service';
import ArticlePlaceholder from 'components/ui/article-placeholder';
import ReactModal from 'react-modal';
import { toast } from 'react-toastify';
import { isReadOnly } from 'utils/helpers';
import { useNavigate } from 'react-router';

const MINIMIZED_STORAGE_KEY = 'partialTaskMinimized';

export default function PartialTask({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();

  const modalContentRef = useRef<HTMLDivElement | null>(null);
  const headingRef = useRef<HTMLHeadingElement | null>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const wasModalOpenRef = useRef(false);

  const [modalIsOpen, setIsOpen] = useState(false);

  function handleCloseModal() {
    onClose();
    setIsOpen(false);
    window.localStorage.setItem(MINIMIZED_STORAGE_KEY, 'true');
  }

  function handleClickCreate() {
    window.localStorage.setItem(MINIMIZED_STORAGE_KEY, 'true');
    navigate('/realizacao-destaque/cadastrar');
  }

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const minimizedFlag = window.localStorage.getItem(MINIMIZED_STORAGE_KEY);
    if (!minimizedFlag) {
      setIsOpen(true);
    } else {
      onClose();
    }
  }, [onClose]);

  const restoreFocus = useCallback(() => {
    // restore previous focus or focus floating button when possible
    try {
      if (previousFocusRef.current && typeof previousFocusRef.current.focus === 'function') {
        previousFocusRef.current.focus();
        return;
      }

      if (typeof document !== 'undefined' && typeof document.body?.focus === 'function') {
        document.body.focus();
      }
    } catch (e) {
      // ignore
    }
  }, []);

  useEffect(() => {
    if (typeof document === 'undefined') return;

    if (modalIsOpen && !wasModalOpenRef.current) {
      wasModalOpenRef.current = true;
      previousFocusRef.current = document.activeElement as HTMLElement | null;
    }

    if (!modalIsOpen && wasModalOpenRef.current) {
      wasModalOpenRef.current = false;
      restoreFocus();
    }
  }, [modalIsOpen, restoreFocus]);

  const getStepFocusOrder = () => {
    const order: HTMLElement[] = [];
    const dialog = modalContentRef.current;
    if (!dialog) return order;

    const cadastrarButton = dialog.querySelector<HTMLElement>('[data-focus-target="cadastrar"]');
    if (cadastrarButton) order.push(cadastrarButton);
    if (headingRef.current) {
      order.push(headingRef.current);
    }

    return order;
  };

  const focusCurrentStep = useCallback(() => {
    if (!modalIsOpen) return;

    const focusOrder = getStepFocusOrder();
    if (focusOrder.length > 0) {
      focusOrder[0].focus();
      return;
    }

    modalContentRef.current?.focus();
  }, [modalIsOpen]);

  useLayoutEffect(() => {
    focusCurrentStep();
  }, [focusCurrentStep, modalIsOpen]);

  return (
    <>
      {modalIsOpen && (
        <ReactModal
          shouldCloseOnOverlayClick={false}
          isOpen={modalIsOpen}
          onAfterOpen={focusCurrentStep}
          onRequestClose={handleCloseModal}
          shouldReturnFocusAfterClose={false}
          aria={{
            labelledby: 'partial-task-title',
            describedby: 'partial-task-description',
          }}
          contentLabel="Você possui uma realização em preenchimento"
          portalClassName="relative z-[70]"
          overlayClassName="fixed z-[120] top-0 left-0 w-full h-full bg-black bg-opacity-80 flex flex-col items-center justify-center"
          className="outline-none"
          ariaHideApp={false}
        >
          <div className="fixed bg-black bg-opacity-40 top-0 left-0 w-screen h-screen p-6 pt-[10vh] flex justify-center overflow-y-auto z-50">
            <div
              role="dialog"
              aria-modal="true"
              ref={modalContentRef}
              tabIndex={-1}
              className="relative border-2 border-solid border-electric-blue bg-oxford-blue rounded-lg py-6 px-8 w-[90%] max-w-[730px] h-fit"
            >
              <Button
                color="primary"
                arialabel="Fechar aviso"
                data-focus-target="close"
                className="absolute -right-5 -top-5  rounded-r-full rounded-l-full w-10 h-10 p-0 flex justify-center items-center"
                onClick={() => {
                  handleCloseModal();
                }}
              >
                <IoIosClose color="white" size={30} />
              </Button>

              <div className="space-y-8 text-base font-thin m-0 w-full">
                <h1
                  id="partial-task-title"
                  ref={headingRef}
                  tabIndex={-1}
                  className="text-yellow text-center leading-none font-bold text-2xl"
                >
                  Você possui uma realização em preenchimento
                </h1>

                <div id="partial-task-description" className="space-y-4">
                  <p className="text-lg">
                    Identificamos que você iniciou o cadastro de uma Realização Destaque, mas ainda não concluiu o envio.
                  </p>
                  <p className="text-lg">
                    Suas informações foram salvas automaticamente para que você possa continuar de onde parou.
                  </p>
                  <p className="text-lg">Clique no botão abaixo para retomar o preenchimento e finalizar o cadastro.</p>
                </div>

                <div className="flex justify-center">
                  <button
                    className="text-alice-blue bg-slate-blue text-sm flex items-center justify-center font-semibold uppercase hover:ring hover:ring-white ring-2 ring-slate-blue rounded py-3 px-10"
                    onClick={() => handleClickCreate()}
                    data-focus-target="cadastrar"
                  >
                    Cadastrar Realização Destaque
                  </button>
                </div>
              </div>
            </div>
          </div>
        </ReactModal>
      )}
    </>
  );
}
